import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { UploaderComponent } from '@component';
import { check } from '@helper';
import {
  CustomErrorStateMatcher,
  CustomEvent,
  GridParameters,
  Scenario,
} from '@model';
import {
  CustomEventService,
  GridProgramService,
  LoaderService,
  ScenarioService,
} from '@service';

@Component({
  selector: 'optima-programs-parameters',
  templateUrl: './programs-parameters.component.html',
  styleUrls: ['./programs-parameters.component.scss'],
})
export class ProgramsParametersComponent implements OnInit {
  @Input() scenario: Scenario;
  @Output() customEventChange = new EventEmitter<Scenario>();
  @ViewChild('uploaderCustomEvent')
  uploaderCustomEvent: UploaderComponent<CustomEvent>;
  formGroup: FormGroup;
  check = check;
  matcher = new CustomErrorStateMatcher();
  customEventsFormDataKeyValue = new Map<string, string>();

  constructor(
    private gridProgramService: GridProgramService,
    public translateService: TranslateService,
    private fb: FormBuilder,
    public scenarioService: ScenarioService,
    private loaderService: LoaderService,
    private customEventService: CustomEventService,
  ) {}

  ngOnInit(): void {
    this.formGroup = this.fb.group({
      curtailment: new FormControl(
        this.scenario.gridParameters.existingLoadCurtailment,
        [Validators.min(0), Validators.max(999999.0), Validators.required],
      ),
      discount: new FormControl(
        this.scenario.gridParameters.annualDiscountFactor,
        [Validators.min(0), Validators.max(20), Validators.required],
      ),
    });

    this.formGroup.valueChanges
      .pipe(
        debounceTime(2000),
        distinctUntilChanged(),
        tap(() => {
          if (this.formGroup.valid) {
            this.update();
          }
        }),
      )
      .subscribe();

    this.customEventsFormDataKeyValue.set('scenario_id', this.scenario.id);
  }

  update(): void {
    const previousValue: GridParameters = JSON.parse(
      JSON.stringify(this.scenario.gridParameters),
    );
    let curtailmentValue;
    let discountValue;
    const cfv = +(<HTMLInputElement>document.getElementById('curtailment'))
      .value;
    const dfv = +(<HTMLInputElement>document.getElementById('discount')).value;
    if (isNaN(cfv) || cfv === null || cfv === undefined) {
      curtailmentValue = previousValue.existingLoadCurtailment;
    } else {
      curtailmentValue = cfv;
    }
    if (isNaN(dfv) || dfv === null || dfv === undefined) {
      discountValue = previousValue.annualDiscountFactor;
    } else {
      discountValue = dfv;
    }
    this.scenario.gridParameters.existingLoadCurtailment = curtailmentValue;
    this.scenario.gridParameters.annualDiscountFactor = discountValue;
    this.formGroup
      .get('curtailment')
      .setValue(this.scenario.gridParameters.existingLoadCurtailment);
    this.formGroup
      .get('discount')
      .setValue(this.scenario.gridParameters.annualDiscountFactor);
    if (
      curtailmentValue === previousValue.existingLoadCurtailment &&
      discountValue === previousValue.annualDiscountFactor
    ) {
      return;
    }
    this.gridProgramService
      .updateGridParameters$(this.scenario.id, this.scenario.gridParameters)
      .subscribe({
        error: () => {
          this.scenario.gridParameters = previousValue;
          this.formGroup
            .get('curtailment')
            .setValue(previousValue.existingLoadCurtailment);
          this.formGroup
            .get('discount')
            .setValue(previousValue.annualDiscountFactor);
        },
      });
  }

  async updateCurrentCustomEvents(customEvent: CustomEvent): Promise<void> {
    const id = this.scenario.customEvent?.id;
    this.scenario.customEvent = customEvent;
    if (!customEvent) {
      this.loaderService.changeLabel('');
      await firstValueFrom(this.customEventService.deleteCustomEvents$(id));
    }
    this.customEventChange.emit(this.scenario);
  }
}
